import styles from "./styles.module.css";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useAppContext } from "../../components/AppContext/AppContext";
import Grid from "@mui/material/Grid";
import {
  Button,
  Card,
  Divider,
  Link,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import Box from "@mui/material/Box";
import PageAdmin from "../../components/PageAdmin/PageAdmin";
import {
  apiAdminEventList,
  apiAdminOrderList,
  apiAdminTowersEventList,
  apiAdminUserList,
  apiEventList,
  apiProductList,
} from "../../api";
import {
  EventType,
  MEMBERSHIP_STATUS_ICON,
  MembershipStatus,
  ORDER_STATUS_ICON,
  OrderStatus,
  PermissionLevel,
  RegistrationStatus,
  UserProductSource,
} from "../../enums";
import { capitalizeFirstLetter } from "../../utils/string";
import IconKeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import { ROUTES } from "../../routes";
import { useNavigate } from "react-router-dom";
import IcconPerson from "@mui/icons-material/Person";
import IconEscalatorWarning from "@mui/icons-material/EscalatorWarning";
import { LineChart } from "@mui/x-charts";
import { getEventIcon } from "../../utils/event";
import IconShoppingCart from "@mui/icons-material/ShoppingCart";
import IconInventory from "@mui/icons-material/Inventory";
import { LoaderClip } from "../../components/LoaderClip/LoaderClip";
import { filterProductsForUser } from "../../utils/product";
import Pagination from "@mui/material/Pagination";
import IconQrCode from "@mui/icons-material/QrCode";

const BACKEND_BASE_URL = new URL(process.env.REACT_APP_TOWERS_API_URL).origin;

function AdminPage() {
  const theme = useTheme();

  const [t, i18n] = useTranslation("common");

  const { user } = useAppContext();
  let navigate = useNavigate();

  const [eventPage, setEventPage] = React.useState(1);
  const [events, setEvents] = React.useState(undefined);
  const [towersByEventId, setTowersByEventId] = React.useState<any>({});
  const [userEvents, setUserEvents] = React.useState(undefined);
  const [statEvents, setStatEvents] = React.useState(undefined);
  const [products, setProducts] = React.useState(undefined);
  const [users, setUsers] = React.useState(undefined);
  const [orders, setOrders] = React.useState(undefined);

  React.useEffect(() => {
    apiAdminEventList(
      eventPage,
      5,
      new Date().toISOString().substring(0, 10),
    ).then((response) => {
      if (response.status === 200) {
        setEvents(response.data);
      }
    });
  }, [eventPage, setEvents]);

  React.useEffect(() => {
    if (events) {
      let currentTowersEvents: any[] = [];
      for (let i = 0; i < events.results.length; i++) {
        apiAdminTowersEventList(events.results[i].id).then((response) => {
          if (response.status === 200) {
            currentTowersEvents.push([
              events.results[i].id,
              response.data.towers,
            ]);
            setTowersByEventId(Object.fromEntries(currentTowersEvents));
          }
        });
      }
    }
  }, [events, setTowersByEventId]);

  React.useEffect(() => {
    apiEventList(
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      true,
    ).then((response) => {
      if (response.status === 200) {
        setUserEvents(response.data);
      }
    });
  }, [setUserEvents]);

  React.useEffect(() => {
    apiEventList(
      1,
      50,
      undefined,
      new Date(Date.now() - 60 * 24 * 60 * 60 * 1000)
        .toISOString()
        .substring(0, 10),
      undefined,
      true,
    ).then((response) => {
      if (response.status === 200) {
        setStatEvents(response.data);
      }
    });
  }, [setStatEvents]);

  React.useEffect(() => {
    apiProductList().then((response) => {
      if (response.status === 200) {
        setProducts(response.data);
      }
    });
  }, [setProducts, i18n.resolvedLanguage]);

  React.useEffect(() => {
    apiAdminUserList(undefined, 10, "-created_at").then((response) => {
      if (response.status === 200) {
        setUsers(response.data);
      }
    });
  }, [setUsers]);

  React.useEffect(() => {
    apiAdminOrderList(undefined, 10).then((response) => {
      if (response.status === 200) {
        setOrders(response.data);
      }
    });
  }, [setOrders, i18n.resolvedLanguage]);

  function handleAdminAttendanceSubmit() {
    navigate(ROUTES["admin-attendance"].path);
  }

  function handleAdminEquipmentSubmit() {
    navigate(ROUTES["admin-equipment"].path);
  }

  function handleAdminUserSubmit() {
    navigate(ROUTES["admin-user"].path);
  }

  function handleAdminEventSubmit() {
    navigate(ROUTES["admin-user"].path);
  }

  function handleAdminHistorySubmit() {
    navigate(ROUTES["admin-history"].path);
  }

  function handleAdminTeamSubmit() {
    navigate(ROUTES["admin-team"].path);
  }

  function handleAdminStatsSubmit() {
    navigate(ROUTES["admin-stats"].path);
  }

  const content = user && (
    <Grid container spacing={4} className={styles.adminGrid}>
      <Grid container size={{ xs: 12, md: 6 }} spacing={4} direction="row">
        <Card variant="outlined" className={styles.adminCard}>
          <Link
            color="textPrimary"
            underline="none"
            component="button"
            onClick={handleAdminAttendanceSubmit}
            className={styles.adminTitleLink}
          >
            <Box className={styles.adminTopBoxLink}>
              <Typography variant="h6" fontWeight="600" component="div">
                {t("pages.admin.attendance-table.title")}
              </Typography>
              <IconKeyboardArrowRight className={styles.adminTitleIcon} />
            </Box>
          </Link>
          {userEvents && userEvents.results.length > 0 ? (
            <Box>
              <List className={styles.adminList}>
                {userEvents.results.map((event: any, i: number, row: any) => {
                  return (
                    <>
                      <ListItemButton disableTouchRipple dense>
                        <ListItemIcon className={styles.eventCardIcon}>
                          {getEventIcon(event.type, event.modules, event.theme)}
                        </ListItemIcon>
                        <Box
                          className={styles.userFamilyListInner}
                          flexDirection={{ xs: "column", lg: "row" }}
                          alignItems={{ xs: "start", lg: "center" }}
                        >
                          <ListItemText
                            className={styles.userFamilyListItem}
                            disableTypography
                            primary={
                              <Typography variant="body2">
                                {event.title +
                                  ((event.type === EventType.REHEARSAL ||
                                    event.type === EventType.WORKSHOP) &&
                                  event.location !== null
                                    ? " — " + event.location.name
                                    : "")}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="body2" color="textSecondary">
                                {capitalizeFirstLetter(
                                  new Date(event.time_from).toLocaleDateString(
                                    i18n.resolvedLanguage,
                                    {
                                      day: "numeric",
                                      month: "long",
                                      year: "numeric",
                                    },
                                  ),
                                ) +
                                  " " +
                                  new Date(event.time_from)
                                    .toTimeString()
                                    .slice(0, 5) +
                                  " ➔ " +
                                  new Date(event.time_to)
                                    .toTimeString()
                                    .slice(0, 5)}
                              </Typography>
                            }
                          ></ListItemText>
                        </Box>
                        <Stack
                          direction="row"
                          spacing={2}
                          marginLeft={{ xs: "0", lg: "16px" }}
                          marginTop={{ xs: "8px", lg: "0" }}
                          marginBottom={{ xs: "8px", lg: "0" }}
                          whiteSpace="nowrap"
                        >
                          <Box className={styles.eventCountBox}>
                            <IcconPerson
                              className={styles.eventCountIcon}
                              color={
                                event.registration_counts.adults.active >= 10 ||
                                event.registration_counts.adults.active >=
                                  event.registration_counts.adults.total / 2
                                  ? "success"
                                  : event.registration_counts.adults.total -
                                        event.registration_counts.adults
                                          .active -
                                        event.registration_counts.adults
                                          .cancelled >=
                                      event.registration_counts.adults.total / 2
                                    ? "secondary"
                                    : "error"
                              }
                            />
                            <Typography variant="body2" color="textSecondary">
                              {[
                                event.registration_counts.adults.active,
                                event.registration_counts.adults.cancelled,
                                event.registration_counts.adults.total -
                                  event.registration_counts.adults.active -
                                  event.registration_counts.adults.cancelled,
                              ].join("/")}
                            </Typography>
                          </Box>
                          <Box className={styles.eventCountBox}>
                            <IconEscalatorWarning
                              className={styles.eventCountIcon}
                              color={
                                event.registration_counts.children.active >=
                                  2 ||
                                event.registration_counts.children.active >=
                                  event.registration_counts.children.total / 2
                                  ? "success"
                                  : event.registration_counts.children.total -
                                        event.registration_counts.children
                                          .active -
                                        event.registration_counts.children
                                          .cancelled >=
                                      event.registration_counts.children.total /
                                        2
                                    ? "secondary"
                                    : "error"
                              }
                            />
                            <Typography variant="body2" color="textSecondary">
                              {[
                                event.registration_counts.children.active,
                                event.registration_counts.children.cancelled,
                                event.registration_counts.children.total -
                                  event.registration_counts.children.active -
                                  event.registration_counts.children.cancelled,
                              ].join("/")}
                            </Typography>
                          </Box>
                        </Stack>
                      </ListItemButton>
                      {i + 1 < row.length && <Divider />}
                    </>
                  );
                })}
              </List>
            </Box>
          ) : (
            <Box className={styles.providerLoader}>
              <LoaderClip />
            </Box>
          )}
        </Card>
      </Grid>
      <Grid container size={{ xs: 12, md: 6 }} spacing={4} direction="row">
        <Card variant="outlined" className={styles.adminCard}>
          <Link
            color="textPrimary"
            underline="none"
            component="button"
            className={styles.adminTitleLink}
            onClick={handleAdminUserSubmit}
          >
            <Box className={styles.adminTopBoxLink}>
              <Typography variant="h6" fontWeight="600" component="div">
                {t("pages.admin.users-table.title")}
              </Typography>
              <IconKeyboardArrowRight className={styles.adminTitleIcon} />
            </Box>
          </Link>
          {users && users.results.length > 0 ? (
            <Box>
              <List className={styles.adminList}>
                {users.results
                  .slice(
                    0,
                    userEvents && userEvents.results.length > 0
                      ? userEvents.results.length === 4
                        ? 7
                        : userEvents.results.length === 5
                          ? 8
                          : userEvents.results.length === 6
                            ? 10
                            : 5
                      : 5,
                  )
                  .map((user: any, i: number, row: any) => {
                    const userTeamIds = user.members
                      ? user.members.map((member: any) => member.team.id)
                      : [];

                    return (
                      <>
                        <ListItemButton disableTouchRipple dense>
                          <Box
                            className={styles.userFamilyListInner}
                            flexDirection={{ xs: "column", lg: "row" }}
                            alignItems={{ xs: "start", lg: "center" }}
                          >
                            <ListItemText
                              className={styles.userFamilyListItem}
                              disableTypography
                              primary={
                                <Typography variant="body2">
                                  {user.lastname
                                    ? user.firstname + " " + user.lastname
                                    : user.firstname}
                                </Typography>
                              }
                            ></ListItemText>
                          </Box>
                          <Stack
                            direction="row"
                            spacing={2}
                            marginLeft={{ xs: "0", lg: "16px" }}
                            marginTop={{ xs: "8px", lg: "0" }}
                            marginBottom={{ xs: "8px", lg: "0" }}
                            whiteSpace="nowrap"
                          >
                            {products &&
                              products.results.length > 0 &&
                              filterProductsForUser(
                                products.results,
                                userTeamIds,
                              ).map((product: any) => {
                                const userProduct =
                                  user.products &&
                                  user.products.find(
                                    (userProduct: any) =>
                                      userProduct.product.id === product.id,
                                  );

                                const orderStatus =
                                  userProduct &&
                                  userProduct.source === UserProductSource.OWNED
                                    ? OrderStatus.COMPLETED
                                    : userProduct && userProduct.order
                                      ? userProduct.order.status
                                      : OrderStatus.CANCELED;

                                return (
                                  <Box className={styles.userListBox}>
                                    <Box
                                      component="span"
                                      className={styles.userListIcon}
                                      color={
                                        orderStatus === OrderStatus.COMPLETED
                                          ? "var(--mui-palette-success-main) !important"
                                          : orderStatus ===
                                              OrderStatus.PROCESSING
                                            ? "var(--mui-palette-secondary-main) !important"
                                            : "var(--mui-palette-error-main) !important"
                                      }
                                    >
                                      {ORDER_STATUS_ICON[orderStatus]}
                                    </Box>
                                    <Typography
                                      variant="body2"
                                      color="textSecondary"
                                    >
                                      {product.name}
                                    </Typography>
                                  </Box>
                                );
                              })}
                            {user.membership && (
                              <Box className={styles.userListBox}>
                                <Box
                                  component="span"
                                  className={styles.userListIcon}
                                  color={
                                    user.membership
                                      ? user.membership.status ===
                                        MembershipStatus.ACTIVE
                                        ? "var(--mui-palette-success-main) !important"
                                        : [
                                              MembershipStatus.REQUESTED,
                                              MembershipStatus.PROCESSING,
                                            ].includes(user.membership.status)
                                          ? "var(--mui-palette-secondary-main) !important"
                                          : "var(--mui-palette-error-main) !important"
                                      : "var(--mui-palette-text-secondary)"
                                  }
                                >
                                  {
                                    MEMBERSHIP_STATUS_ICON[
                                      user.membership.status
                                    ]
                                  }
                                </Box>
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                >
                                  {t("pages.admin.users-table.membership")}
                                </Typography>
                              </Box>
                            )}
                          </Stack>
                        </ListItemButton>
                        {i + 1 < row.length && <Divider />}
                      </>
                    );
                  })}
              </List>
            </Box>
          ) : (
            <Box className={styles.providerLoader}>
              <LoaderClip />
            </Box>
          )}
        </Card>
      </Grid>
      <Grid
        container
        size={{ xs: 12, md: 12 }}
        spacing={2}
        direction="column"
        justifyContent="center"
      >
        <Card variant="outlined" className={styles.adminCard}>
          <Link
            color="textPrimary"
            underline="none"
            component="button"
            className={styles.adminTitleLink}
            // onClick={handleAdminEventSubmit}
          >
            <Box className={styles.adminTopBoxLink}>
              <Typography variant="h6" fontWeight="600" component="div">
                {t("pages.admin.events-table.title")}
              </Typography>
              {/* <IconKeyboardArrowRight className={styles.adminTitleIcon} /> */}
            </Box>
          </Link>
          {events && events.results.length > 0 ? (
            <Box>
              <List className={styles.adminList}>
                {events.results.map((event: any, i: number, row: any) => {
                  return (
                    <>
                      <ListItemButton disableTouchRipple dense>
                        <ListItemIcon className={styles.eventCardIcon}>
                          {getEventIcon(event.type, event.modules, event.theme)}
                        </ListItemIcon>
                        <Box
                          className={styles.userFamilyListInner}
                          flexDirection={{ xs: "column", lg: "row" }}
                          alignItems={{ xs: "start", lg: "center" }}
                        >
                          <ListItemText
                            className={styles.userFamilyListItem}
                            disableTypography
                            primary={
                              <Typography variant="body2">
                                {event.title +
                                  ((event.type === EventType.REHEARSAL ||
                                    event.type === EventType.WORKSHOP) &&
                                  event.location !== null
                                    ? " — " + event.location.name
                                    : "")}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="body2" color="textSecondary">
                                {capitalizeFirstLetter(
                                  new Date(event.time_from).toLocaleDateString(
                                    i18n.resolvedLanguage,
                                    {
                                      day: "numeric",
                                      month: "long",
                                      year: "numeric",
                                    },
                                  ),
                                ) +
                                  " " +
                                  new Date(event.time_from)
                                    .toTimeString()
                                    .slice(0, 5) +
                                  " ➔ " +
                                  new Date(event.time_to)
                                    .toTimeString()
                                    .slice(0, 5)}
                              </Typography>
                            }
                          ></ListItemText>

                          {towersByEventId && event.id in towersByEventId && (
                            <Stack
                              direction="row"
                              spacing={2}
                              marginLeft={{ xs: "0", lg: "8px" }}
                              marginTop={{ xs: "8px", lg: "0" }}
                              marginBottom={{ xs: "8px", lg: "0" }}
                              whiteSpace="nowrap"
                              display={{ xs: "none", md: "flex" }}
                            >
                              {towersByEventId[event.id].map((tower: any) => {
                                return (
                                  <Box className={styles.towerBox}>
                                    <Typography
                                      variant="body1"
                                      color={
                                        tower.is_published
                                          ? "success"
                                          : "textSecondary"
                                      }
                                      fontWeight={
                                        tower.is_published ? "600" : undefined
                                      }
                                    >
                                      {tower.name}
                                    </Typography>
                                  </Box>
                                );
                              })}
                            </Stack>
                          )}
                          {event.require_signup && event.require_approve && (
                            <Stack
                              direction="row"
                              spacing={2}
                              marginLeft={{ xs: "0", lg: "16px" }}
                              marginTop={{ xs: "8px", lg: "0" }}
                              marginBottom={{ xs: "8px", lg: "0" }}
                              whiteSpace="nowrap"
                            >
                              <Button
                                variant="contained"
                                type="submit"
                                style={{ width: "auto" }}
                                disableElevation
                                href={ROUTES[
                                  "admin-event-attendance"
                                ].path.replace(":id", event.id)}
                              >
                                {t("pages.admin.events-table.attendance")}
                              </Button>
                              <Button
                                variant="contained"
                                type="submit"
                                style={{ width: "auto" }}
                                disableElevation
                                href={ROUTES["admin-event-token"].path.replace(
                                  ":id",
                                  event.id,
                                )}
                              >
                                <IconQrCode />
                              </Button>
                            </Stack>
                          )}
                        </Box>
                      </ListItemButton>
                      {i + 1 < row.length && <Divider />}
                    </>
                  );
                })}
              </List>
            </Box>
          ) : (
            <Box className={styles.providerLoader}>
              <LoaderClip />
            </Box>
          )}
        </Card>
        {events &&
          events.results.length > 0 &&
          (eventPage !== 1 || events.count > events.results.length) && (
            <Stack alignItems="center">
              <Pagination
                count={Math.ceil(events.count / 5)}
                onChange={(e: any, value: number) => setEventPage(value)}
              />
            </Stack>
          )}
      </Grid>
      <Grid container size={{ xs: 12, md: 6 }} spacing={4} direction="row">
        <Card variant="outlined" className={styles.adminCard}>
          <Link
            color="textPrimary"
            underline="none"
            component="button"
            className={styles.adminTitleLink}
            onClick={handleAdminEquipmentSubmit}
          >
            <Box className={styles.adminTopBoxLink}>
              <Typography variant="h6" fontWeight="600" component="div">
                {t("pages.admin.equipment-table.title")}
              </Typography>
              <IconKeyboardArrowRight className={styles.adminTitleIcon} />
            </Box>
          </Link>
          {products && products.results.length > 0 ? (
            <Box>
              <List className={styles.adminList}>
                {products.results.map((product: any, i: number, row: any) => {
                  return (
                    <>
                      <ListItemButton disableTouchRipple dense>
                        <ListItemIcon className={styles.eventCardIcon}>
                          {product.images && product.images.length > 0 && (
                            <img
                              src={BACKEND_BASE_URL + product.images[0].picture}
                              alt={product.name}
                              className={styles.eventCardImage}
                            />
                          )}
                        </ListItemIcon>
                        <Box
                          className={styles.userFamilyListInner}
                          flexDirection={{ xs: "column", lg: "row" }}
                          alignItems={{ xs: "start", lg: "center" }}
                        >
                          <ListItemText
                            className={styles.userFamilyListItem}
                            disableTypography
                            primary={
                              <Typography variant="body2">
                                {product.name}
                              </Typography>
                            }
                          ></ListItemText>
                        </Box>
                        <Stack
                          direction="row"
                          spacing={2}
                          marginLeft={{ xs: "0", lg: "16px" }}
                          marginTop={{ xs: "8px", lg: "0" }}
                          marginBottom={{ xs: "8px", lg: "0" }}
                          whiteSpace="nowrap"
                        >
                          {product.stock_out_pending ? (
                            <Box className={styles.stockCountBox}>
                              <IconShoppingCart
                                className={styles.stockCountIcon}
                              />
                              <Typography variant="body2" color="textSecondary">
                                {product.stock_out_pending}
                              </Typography>
                            </Box>
                          ) : undefined}
                          <Box className={styles.stockCountBox}>
                            <IconInventory className={styles.stockCountIcon} />
                            <Typography variant="body2" color="textSecondary">
                              {product.stock}
                            </Typography>
                          </Box>
                        </Stack>
                      </ListItemButton>
                      {i + 1 < row.length && <Divider />}
                    </>
                  );
                })}
              </List>
            </Box>
          ) : (
            <Box className={styles.providerLoader}>
              <LoaderClip />
            </Box>
          )}
        </Card>
      </Grid>
      <Grid container size={{ xs: 12, md: 6 }} spacing={4} direction="row">
        <Card variant="outlined" className={styles.adminCard}>
          <Link
            color="textPrimary"
            underline="none"
            component="button"
            className={styles.adminTitleLink}
          >
            <Box className={styles.adminTopBoxLink}>
              <Typography variant="h6" fontWeight="600" component="div">
                {t("pages.admin.orders-table.title")}
              </Typography>
              {/*<IconKeyboardArrowRight className={styles.adminTitleIcon} />*/}
            </Box>
          </Link>
          {orders && orders.results.length > 0 ? (
            <Box>
              <List className={styles.adminList}>
                {orders.results
                  .slice(
                    0,
                    products && products.results.length > 0
                      ? products.results.length
                      : 5,
                  )
                  .filter(
                    (order: any) =>
                      order.products &&
                      order.products.length > 0 &&
                      order.entity,
                  )
                  .map((order: any, i: number, row: any) => {
                    const userFirstname =
                      order.entity.firstname ||
                      (order.entity.user && order.entity.user.firstname);
                    const userLastname =
                      order.entity.lastname ||
                      (order.entity.user && order.entity.user.lastname);

                    return (
                      <>
                        <ListItemButton disableTouchRipple dense>
                          <Box
                            className={styles.userFamilyListInner}
                            flexDirection={{ xs: "column", lg: "row" }}
                            alignItems={{ xs: "start", lg: "center" }}
                          >
                            <ListItemText
                              className={styles.userFamilyListItem}
                              disableTypography
                              primary={
                                <Typography variant="body2">
                                  {userLastname
                                    ? userFirstname + " " + userLastname
                                    : userFirstname}
                                </Typography>
                              }
                            ></ListItemText>
                          </Box>
                          <Box>
                            <Stack
                              direction="row"
                              spacing={2}
                              gap={0.5}
                              marginLeft={{ xs: "0", lg: "16px" }}
                              marginTop={{ xs: "8px", lg: "0" }}
                              marginBottom={{ xs: "8px", lg: "0" }}
                              whiteSpace="nowrap"
                              flexWrap="wrap"
                              justifyContent="end"
                            >
                              {order.products.map((orderProduct: any) => {
                                return (
                                  <Box className={styles.userListBox}>
                                    <Box
                                      component="span"
                                      className={styles.userListIcon}
                                      color={
                                        orderProduct.quantity ===
                                        orderProduct.quantity_given
                                          ? "var(--mui-palette-success-main) !important"
                                          : "var(--mui-palette-secondary-main) !important"
                                      }
                                    >
                                      {
                                        ORDER_STATUS_ICON[
                                          orderProduct.quantity ===
                                          orderProduct.quantity_given
                                            ? OrderStatus.COMPLETED
                                            : OrderStatus.PROCESSING
                                        ]
                                      }
                                    </Box>
                                    <Typography
                                      variant="body2"
                                      color="textSecondary"
                                    >
                                      {orderProduct.quantity +
                                        " x " +
                                        orderProduct.size.product.name +
                                        " (" +
                                        orderProduct.size.size +
                                        ")"}
                                    </Typography>
                                  </Box>
                                );
                              })}
                            </Stack>
                          </Box>
                        </ListItemButton>
                        {i + 1 < row.length && <Divider />}
                      </>
                    );
                  })}
              </List>
            </Box>
          ) : (
            <Box className={styles.providerLoader}>
              <LoaderClip />
            </Box>
          )}
        </Card>
      </Grid>
      {user.permission_level >= PermissionLevel.SUPERADMIN && (
        <Grid container size={{ xs: 12, md: 6 }} spacing={4} direction="row">
          <Card variant="outlined" className={styles.adminCard}>
            <Link
              color="textPrimary"
              underline="none"
              component="button"
              className={styles.adminTitleLink}
              onClick={handleAdminHistorySubmit}
            >
              <Box className={styles.adminTopBoxLink}>
                <Typography variant="h6" fontWeight="600" component="div">
                  {t("pages.admin.history-table.title")}
                </Typography>
                <IconKeyboardArrowRight className={styles.adminTitleIcon} />
              </Box>
            </Link>
          </Card>
        </Grid>
      )}
      {user.permission_level >= PermissionLevel.SUPERADMIN && (
        <Grid container size={{ xs: 12, md: 6 }} spacing={4} direction="row">
          <Card variant="outlined" className={styles.adminCard}>
            <Link
              color="textPrimary"
              underline="none"
              component="button"
              className={styles.adminTitleLink}
              onClick={handleAdminTeamSubmit}
            >
              <Box className={styles.adminTopBoxLink}>
                <Typography variant="h6" fontWeight="600" component="div">
                  {t("pages.admin.team-table.title")}
                </Typography>
                {/* <IconKeyboardArrowRight className={styles.adminTitleIcon} /> */}
              </Box>
            </Link>
          </Card>
        </Grid>
      )}
      <Grid container size={{ xs: 12, md: 12 }} spacing={4} direction="row">
        <Card variant="outlined" className={styles.adminCard}>
          <Link
            color="textPrimary"
            underline="none"
            component="button"
            className={styles.adminTitleLink}
            onClick={handleAdminStatsSubmit}
          >
            <Box className={styles.adminTopBoxLink}>
              <Typography variant="h6" fontWeight="600" component="div">
                {t("pages.admin.stats-table.title")}
              </Typography>
              <IconKeyboardArrowRight className={styles.adminTitleIcon} />
            </Box>
          </Link>
          {statEvents && statEvents.results.length > 0 ? (
            <Box className={styles.adminBoxCharts}>
              <Box mt={1}>
                <Typography
                  variant="body1"
                  fontWeight="600"
                  component="div"
                  textAlign="center"
                >
                  {t("pages.admin.stats-table.attendance-title")}
                </Typography>
                <LineChart
                  margin={{
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: -32,
                  }}
                  xAxis={[
                    {
                      data: statEvents.results.map(
                        (event: any) => new Date(event.time_from),
                      ),
                      valueFormatter: (date: string) =>
                        new Date(date).toISOString().substring(0, 10),
                      min: new Date(statEvents.results[0].time_from),
                      max: new Date(
                        statEvents.results[
                          statEvents.results.length - 1
                        ].time_from,
                      ),
                    },
                  ]}
                  series={[
                    {
                      id: RegistrationStatus.CANCELLED,
                      label: t("enums.registration-status.30"),
                      data: statEvents.results.map(
                        (event: any) =>
                          event.registration_counts.adults.cancelled +
                          event.registration_counts.children.cancelled,
                      ),
                      area: true,
                      showMark: false,
                      stack: "expand",
                      color: theme.palette.error.main,
                    },
                    {
                      id: 0,
                      label: t("enums.registration-status.0"),
                      data: statEvents.results.map(
                        (event: any) =>
                          event.registration_counts.adults.total +
                          event.registration_counts.children.total -
                          event.registration_counts.adults.active -
                          event.registration_counts.children.active -
                          event.registration_counts.adults.cancelled -
                          event.registration_counts.children.cancelled,
                      ),
                      area: true,
                      showMark: false,
                      stack: "expand",
                      color: theme.palette.secondary.main,
                    },
                    {
                      id: RegistrationStatus.ACTIVE,
                      label: t("enums.registration-status.20"),
                      data: statEvents.results.map(
                        (event: any) =>
                          event.registration_counts.adults.active +
                          event.registration_counts.children.active,
                      ),
                      area: true,
                      showMark: false,
                      stack: "expand",
                      color: theme.palette.success.main,
                    },
                  ]}
                  yAxis={[
                    {
                      label: "Users",
                    },
                  ]}
                  height={300}
                />
              </Box>
            </Box>
          ) : (
            <Box className={styles.providerLoader}>
              <LoaderClip />
            </Box>
          )}
        </Card>
      </Grid>
    </Grid>
  );

  return (
    <PageAdmin
      title={t("pages.admin.title")}
      content={content}
      finishedRegistration={true}
    />
  );
}

export default AdminPage;
